// =============================================================================
// app/src/lib/atualizacao.ts — "tem versão nova", sem ninguém apertar nada.
//
// Pedido do dono (22/08/2026): "gostaria de não ter que ficar apertando
// Ctrl+Shift+R em toda atualização do sistema".
//
// ERAM DOIS PROBLEMAS, e este arquivo resolve o segundo.
//
// O primeiro estava no servidor: as rotas do app (/, /vendas, /caixa) devolvem
// o index.html mas não terminam em ".html", então escapavam da regra de cache
// do Caddy e saíam SEM Cache-Control. Sem esse cabeçalho o navegador guarda por
// conta própria (cache heurístico) e serve o HTML velho — que aponta para o
// pacote de JavaScript velho. Corrigido no Caddyfile: página revalida sempre.
//
// O segundo é este: o balcão deixa a aba ABERTA o dia inteiro. Mesmo com o
// servidor certo, quem não recarrega continua rodando o código de ontem. O
// service worker até se atualiza sozinho (skipWaiting + clients.claim), mas a
// página que já está na tela segue com o JavaScript antigo em memória.
//
// E HAVIA UM TERCEIRO, que só apareceu em 01/09/2026: nada disto acontecia.
// O aviso pende do `updatefound`, e o navegador só dispara `updatefound` quando
// o /sw.js volta do servidor com BYTES diferentes. O public/sw.js é estático —
// saía idêntico em todo deploy, então nunca havia service worker novo, nunca
// havia aviso, e o dono continuou apertando Ctrl+Shift+R. Medido com Caddy e
// navegador de verdade; corrigido em app/carimbo.ts, que é onde está o porquê.
//
// POR QUE NÃO RECARREGAR SOZINHO: a atualização chegaria no meio de uma venda,
// com o vendedor digitando o nome do cliente. O rascunho salva o carrinho, mas
// a tela pular sozinha durante o atendimento assusta — e assustar o balcão é
// como se perde a confiança no sistema. Então: avisa e deixa a pessoa escolher
// o momento. Um toque, e pronto.
// =============================================================================

/** Evento que o Layout escuta para mostrar a faixa de "versão nova". */
export const EVENTO_ATUALIZACAO = 'serginho:atualizacao-disponivel';

/** De quanto em quanto tempo perguntar ao servidor se há versão nova.
 *  15 minutos: o suficiente para a loja pegar a correção do dia sem ficar
 *  batendo no servidor com a aba parada. */
export const INTERVALO_CHECAGEM_MS = 15 * 60 * 1000;

/** Quem dispara o aviso: em produção é a própria janela; no teste, um
 *  EventTarget qualquer — é o que deixa esta lógica ser provada sem navegador
 *  (mesmo motivo do armazém injetável do rascunho da venda). */
type AlvoDoAviso = { dispatchEvent: (e: Event) => boolean };

function avisar(alvo: AlvoDoAviso): void {
  alvo.dispatchEvent(new CustomEvent(EVENTO_ATUALIZACAO));
}

/**
 * Liga a vigilância de versão nova. Chamado uma vez, no arranque.
 *
 * `registro` é o ServiceWorkerRegistration; o tipo vem solto para esta função
 * poder ser testada sem navegador.
 */
export function vigiarAtualizacoes(
  registro: {
    addEventListener: (t: string, h: () => void) => void;
    installing: { state: string; addEventListener: (t: string, h: () => void) => void } | null;
    waiting: unknown;
    update: () => Promise<unknown> | void;
  },
  opcoes: {
    /** Já existe SW controlando? Só nesse caso "instalado" significa ATUALIZAÇÃO
     *  — na primeira visita ele instala pela primeira vez, e avisar ali seria
     *  mentira. */
    temControlador: boolean;
    aCada?: number;
    janela?: {
      setInterval: (f: () => void, ms: number) => unknown;
      addEventListener: (t: string, h: () => void) => void;
      dispatchEvent: (e: Event) => boolean;
    };
    documento?: { visibilityState: string };
  },
): void {
  const janela = opcoes.janela ?? window;
  // Fora do navegador (teste em node) não há `document`, e resolver a
  // constante direto derrubaria o módulo no import.
  const documento = opcoes.documento
    ?? (typeof document === 'undefined' ? { visibilityState: 'visible' } : document);

  // Já havia uma versão esperando quando o app abriu (o navegador guardou de
  // uma visita anterior): avisa na hora.
  if (registro.waiting && opcoes.temControlador) avisar(janela);

  registro.addEventListener('updatefound', () => {
    const novo = registro.installing;
    if (!novo) return;
    novo.addEventListener('statechange', () => {
      // 'installed' com controlador é troca de versão. Sem controlador é a
      // primeira instalação — silêncio.
      if (novo.state === 'installed' && opcoes.temControlador) avisar(janela);
    });
  });

  const checar = () => { try { void registro.update(); } catch { /* sem rede */ } };
  janela.setInterval(checar, opcoes.aCada ?? INTERVALO_CHECAGEM_MS);
  // Quem volta para a aba depois do almoço merece a versão da tarde: checar ao
  // reaparecer pega o caso comum sem esperar o próximo intervalo.
  janela.addEventListener('visibilitychange', () => {
    if (documento.visibilityState === 'visible') checar();
  });
}
